import { useCallback, useEffect, useMemo, useState } from 'react';
import apiClient from '../../../apiTools/apiClient';

/**
 * useArchitectureCounts — live per-LitGroup counts for the Architecture Explorer.
 *
 * ─── ⛔ THE STATE MACHINE IS THE POINT: A MISSING COUNT IS NEVER ZERO ────────────────────────
 *
 * "0 Characters" reads as a fact about the book. It is not — it is what a failed fetch looks like
 * if you let it. Every silent failure this codebase has hunted down had that shape: an S3 delete
 * returning 204 for a key it never touched, an orphan query calling 3 GB of audio garbage. So this
 * hook reports four distinct states and the component renders numbers in exactly ONE of them:
 *
 *   'off'        — generic view. Nobody asked for counts; structure only.
 *   'loading'    — asked, no answer yet. Show the structure, no numbers.
 *   'error'      — asked and failed. Say so. Still no numbers.
 *   'ready'      — counted. Numbers are real and the moment they were taken is known.
 *
 * ⚠️ `counts` is `{}` in every state but 'ready'. The merge helper shows nothing for an id it
 * cannot find, so an empty object cannot become a screen full of zeroes.
 */
export function useArchitectureCounts({ enabled }) {
  const [litGroups, setLitGroups] = useState([]);
  const [selected, setSelected] = useState(null);
  const [status, setStatus] = useState(enabled ? 'loading' : 'off');
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);

  // Per-group cache, so flipping back to a group already counted is instant and costs no query.
  // ⚠️ Deliberately NOT persisted: these are live numbers and a cached count surviving a reload
  // would be presented as current while being arbitrarily old.
  const [cache, setCache] = useState({});

  /* ── the selector's options ─────────────────────────────────────────── */
  useEffect(() => {
    if (!enabled) { setStatus('off'); return undefined; }
    let cancelled = false;

    (async () => {
      setStatus('loading');
      setError(null);
      try {
        const { data } = await apiClient.get('/architecture/litgroups');
        if (cancelled) return;
        const groups = data?.litGroups || [];
        setLitGroups(groups);
        if (!groups.length) {
          // A signed-in creator with no series is not an error — there is simply nothing to count.
          setStatus('empty');
          return;
        }
        setSelected((prev) => prev || groups[0].uuid);
      } catch (err) {
        if (cancelled) return;
        setError(err?.response?.data?.error || err.message);
        setStatus('error');
      }
    })();

    return () => { cancelled = true; };
  }, [enabled]);

  /* ── counts for the selected group ──────────────────────────────────── */
  useEffect(() => {
    if (!enabled || !selected) return undefined;
    let cancelled = false;

    if (cache[selected]) {
      setPayload(cache[selected]);
      setStatus('ready');
      return undefined;
    }

    (async () => {
      setStatus('loading');
      setError(null);
      try {
        const { data } = await apiClient.get(`/architecture/counts/${encodeURIComponent(selected)}`);
        if (cancelled) return;
        setPayload(data);
        setCache((c) => ({ ...c, [selected]: data }));
        setStatus('ready');
      } catch (err) {
        if (cancelled) return;
        // ⛔ Clear the payload. Leaving the previous group's numbers on screen after a failed
        // switch would attribute one series' counts to another — worse than showing none.
        setPayload(null);
        setError(err?.response?.data?.error || err.message);
        setStatus('error');
      }
    })();

    return () => { cancelled = true; };
  }, [enabled, selected, cache]);

  const counts = useMemo(
    () => (status === 'ready' && payload ? payload.counts || {} : {}),
    [status, payload]
  );

  const select = useCallback((uuid) => { setSelected(uuid); }, []);

  return {
    status,                  // off | loading | ready | error | empty
    error,
    litGroups,
    selected,
    select,
    counts,
    litGroupName: payload?.litGroupName || null,
    countedAt: payload?.countedAt || null,
    partial: !!payload?.partial
  };
}

/**
 * useAdminNotes — editorial notes for admins, fetched rather than bundled.
 *
 * ⭐ THE FETCH IS THE HARDENING. This content used to be a constant in architectureModel.js behind
 * a conditional render: the check chose whether to DRAW it, not whether to DELIVER it, so every
 * visitor to a public article already had the text in their bundle. Now the server decides, and a
 * non-admin receives 403 with nothing to read.
 *
 * ⚠️ Fails CLOSED and silently. A failure means no notes render — never a partial or a placeholder
 * hinting at what is hidden. `enabled` is the client's guess at admin-ness and is only an
 * optimisation to avoid a pointless 403; the server's check is what actually decides.
 */
/**
 * ⚠️ Module scope, not inside the hook. Declared in the hook body it would be a fresh object every
 * render, which makes it an unstable dependency of the effect below and invites an
 * exhaustive-deps warning that someone would silence rather than fix.
 */
const EMPTY_DETAIL = Object.freeze({
  noteSections: [], storage: null, nodeNotes: {}, standaloneNotes: {}, sequenceNotes: {},
  kindNotes: {}, standaloneKeys: {}, sequenceMakes: {}
});

export function useAdminNotes({ enabled }) {
  const [detail, setDetail] = useState(EMPTY_DETAIL);

  useEffect(() => {
    if (!enabled) { setDetail(EMPTY_DETAIL); return undefined; }
    let cancelled = false;

    (async () => {
      try {
        const { data } = await apiClient.get('/architecture/admin-detail');
        if (!cancelled) {
          setDetail({
            noteSections: data?.noteSections || [],
            storage: data?.storage || null,
            nodeNotes: data?.nodeNotes || {},
            standaloneNotes: data?.standaloneNotes || {},
            sequenceNotes: data?.sequenceNotes || {},
            kindNotes: data?.kindNotes || {},
            standaloneKeys: data?.standaloneKeys || {},
            sequenceMakes: data?.sequenceMakes || {}
          });
        }
      } catch (_) {
        // Deliberately quiet: a 403 here is the system working. Logging "forbidden" on every
        // non-admin page view would be noise that invites someone to "fix" it.
        if (!cancelled) setDetail(EMPTY_DETAIL);
      }
    })();

    return () => { cancelled = true; };
  }, [enabled]);

  return detail;
}

/**
 * useArchitectureStructure — the node trees, kinds and sequences, fetched rather than bundled.
 *
 * ⛔ THE STRUCTURE IS NOT IN THE BUNDLE ANY MORE. It was, until 2026-09-12: every visitor to a
 * public info article downloaded the complete map — every node, every relationship name, all five
 * sequences — and could read it in devtools. Conditioning the render would have changed nothing,
 * because a conditional render decides what is DRAWN and never what is DELIVERED.
 *
 * ⚠️ Fails CLOSED: no structure means the component shows the public blurb and nothing else. That is
 * also what a not-signed-in visitor sees, so there is no state in which a stranger gets a partial
 * map or a placeholder describing what is missing.
 */
export function useArchitectureStructure({ enabled }) {
  const [structure, setStructure] = useState(null);
  const [status, setStatus] = useState(enabled ? 'loading' : 'off');

  useEffect(() => {
    if (!enabled) { setStructure(null); setStatus('off'); return undefined; }
    let cancelled = false;

    (async () => {
      setStatus('loading');
      try {
        const { data } = await apiClient.get('/architecture/structure');
        if (cancelled) return;
        setStructure(data || null);
        setStatus(data ? 'ready' : 'error');
      } catch (_) {
        // Quiet, like the admin fetch: a 401 here is the system working as intended.
        if (!cancelled) { setStructure(null); setStatus('error'); }
      }
    })();

    return () => { cancelled = true; };
  }, [enabled]);

  return { structure, status };
}

/**
 * Return a copy of `node` with live counts merged in by `id`, recursively.
 *
 * ⭐ THE SERVER'S RESPONSE IS THE AUTHORITY on what is countable. An id the server did not send
 * keeps whatever the model had — `null` — and therefore renders nothing. That is why the model
 * needs no "is countable" flag: there is no second list to fall out of step with the first.
 *
 * ⚠️ Returns a NEW object rather than mutating. The model is a module-level constant shared by
 * every mount of this component; writing counts into it would leak one reader's series into
 * another's view on the same page, and would survive a switch back to the generic mode.
 */
export function withCounts(node, counts) {
  if (!node) return node;
  const live = counts && Object.prototype.hasOwnProperty.call(counts, node.id)
    ? counts[node.id]
    : undefined;

  const next = { ...node };
  if (typeof live === 'number') next.count = live;
  if (node.children) next.children = node.children.map((c) => withCounts(c, counts));
  return next;
}

/**
 * Attach server-supplied internal caveats as `adminNote`, keyed by node `id`.
 *
 * ⭐ SAME SHAPE AS withCounts, AND FOR THE SAME REASON. A node carries a public `note` describing
 * what the thing IS, and an admin note describing what will bite. Both audiences get an accurate
 * answer instead of one of them getting the wrong register.
 *
 * ⛔ The caveats live server-side. An `adminNote` field in architectureModel.js would ship to every
 * visitor and be readable in devtools — which is the mistake this component has now made three
 * times and should not make a fourth.
 *
 * ⚠️ Returns a NEW object, never mutating: the model trees are module-level constants shared by
 * every mount, so writing into them would leak admin text into a non-admin's view on the same page
 * and survive a sign-out.
 */
export function withAdminNotes(node, nodeNotes) {
  return withNoteField(node, nodeNotes, 'adminNote');
}

/**
 * The same merge for any tier's note field.
 *
 * ⭐ Written once because there are now TWO tiers arriving from two endpoints — creator notes with
 * the structure, admin notes behind requireAdmin — and a node can carry both. Copying the walk
 * would mean two recursions that could drift apart, and the recursion is the part that must not.
 *
 * ⚠️ `hasOwnProperty` rather than a truthiness check: an empty-string note is a content bug worth
 * seeing, not something to silently drop.
 */
export function withNoteField(node, notes, field) {
  if (!node) return node;
  const extra = notes && Object.prototype.hasOwnProperty.call(notes, node.id)
    ? notes[node.id]
    : undefined;

  const next = { ...node };
  if (extra) next[field] = extra;
  if (node.children) next.children = node.children.map((c) => withNoteField(c, notes, field));
  return next;
}
